import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { EmptyStateAlt } from '@ovComponents/2-component/emptyState/emptyState';
import { PieChartSecurities } from '@ovComponents/3-pattern/pieChartSecurities/pieChartSecurities';
import { SecuritiesSelectionTable } from '@ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import DownloadModelPortfolioSecurities from '@ovComponents/5-page/modelPortfolios/components/downloadModelPortfolioSecurities';
import { Box, Skeleton, Typography } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getHouseholdModelPortfolioAllocationData } from '../householdModelPortfolio.allocation.utils';
import type {
  HouseholdModelPortfolioAction,
  HouseholdModelPortfolioModel,
} from '../householdModelPortfolio.types';
import { isHouseholdModelPortfolioDetailsMissing } from '../householdModelPortfolio.utils';

type HouseholdModelPortfolioAllocationProps = {
  action: HouseholdModelPortfolioAction;
  model: HouseholdModelPortfolioModel | null;
  modelDetailsLoading: boolean;
  modelDetailsError?: Error;
};

const HouseholdModelPortfolioAllocationSkeleton = () => (
  <Box
    data-testid='household-model-portfolio-allocation-loading'
    sx={{
      display: 'flex',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 300,
      }}
    >
      <Skeleton variant='rounded' width='100%' height={280} />
    </Box>
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Skeleton variant='rectangular' width='30%' height={28} sx={{ mb: 2 }} />
      <Skeleton variant='rounded' width='100%' height={220} />
    </Box>
  </Box>
);

const hasHouseholdModelPortfolioAllocationContent = (
  model: HouseholdModelPortfolioModel | null,
  allocation: ReturnType<typeof getHouseholdModelPortfolioAllocationData>,
): boolean =>
  allocation.securities.length > 0 ||
  (allocation.isAssetClassStrategy &&
    (model?.assetClassBranches?.length ?? 0) > 0);

export const HouseholdModelPortfolioAllocation = ({
  action,
  model,
  modelDetailsLoading,
  modelDetailsError,
}: HouseholdModelPortfolioAllocationProps) => {
  const { t } = useTranslation(['components', 'modelPortfolios', 'shared']);
  const hasAttachedModel = action === 'edit';
  const detailsMissing = isHouseholdModelPortfolioDetailsMissing({
    action,
    model,
    modelDetailsLoading,
    modelDetailsError,
  });

  const allocation = useMemo(
    () => getHouseholdModelPortfolioAllocationData(model),
    [model],
  );

  const hasAllocationContent = hasHouseholdModelPortfolioAllocationContent(
    model,
    allocation,
  );

  if (!hasAttachedModel) {
    return null;
  }

  return (
    <Box
      data-testid='household-model-portfolio-allocation-section'
      sx={{
        mt: 3,
      }}
    >
      {modelDetailsLoading ? (
        <HouseholdModelPortfolioAllocationSkeleton />
      ) : modelDetailsError || detailsMissing ? (
        <Box data-testid='household-model-portfolio-allocation-error'>
          <EmptyStateAlt
            sx={{ height: '112px' }}
            title={t('components:householdModelPortfolio.allocation.error')}
            subtitle={t('shared:errorStateSubText')}
          />
        </Box>
      ) : !hasAllocationContent ? (
        <Box data-testid='household-model-portfolio-allocation-no-securities'>
          <EmptyStateAlt
            sx={{ height: '112px' }}
            title={t(
              'components:householdModelPortfolio.allocation.noSecurities',
            )}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 300,
            }}
          >
            <Card>
              <CardContent>
                <PieChartSecurities
                  testId='household-model-portfolio-allocation-pie-chart'
                  variant='none'
                  securities={allocation.pieChartProps.securities}
                  modelPortfolioName={
                    allocation.pieChartProps.modelPortfolioName
                  }
                  portfolioTemplate={allocation.pieChartProps.portfolioTemplate}
                  assetClassLevel={allocation.pieChartProps.assetClassLevel}
                />
              </CardContent>
            </Card>
          </Box>

          <Box
            sx={{
              flex: 1,
            }}
          >
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    data-testid='household-model-portfolio-allocation-title'
                    variant='headingSmall'
                  >
                    {t('modelPortfolios:portfolio.tabs.assetAllocation')}
                  </Typography>
                  {model?.id ? (
                    <Box data-testid='household-model-portfolio-allocation-download'>
                      <DownloadModelPortfolioSecurities
                        modelPortfolioId={model.id}
                      />
                    </Box>
                  ) : null}
                </Box>
              </CardContent>

              <SecuritiesSelectionTable
                editable={false}
                sortable
                securities={allocation.tableSecurities}
                options={allocation.tableOptions}
                groupedByAssetClass={
                  allocation.isAssetClassStrategy &&
                  allocation.groupedSecurities
                    ? allocation.groupedSecurities
                    : undefined
                }
                unassignedSecurities={
                  allocation.isAssetClassStrategy
                    ? allocation.unassignedSecurities
                    : undefined
                }
                securitySelectAssetClassLevel={
                  allocation.isAssetClassStrategy
                    ? model?.assetClassLevel
                    : undefined
                }
                ignoreBorderStyling
              />
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
};
